/**
 * The EW-4 sections of an earthworks job's own page (fieldiq-api#1284):
 * daily logs, rent-ins, permits and hazmat, and the job's warnings banner.
 *
 * Each is a full-width panel. A daily log is a record of its own, so its row
 * opens /earthworks/daily-logs/:id; rent-ins, permits and hazmat assessments
 * are children of the job, so an add form opens inline in the panel. Writes
 * are dispatch roles, and a closed job takes none. A refusal reads as one
 * sentence from `ewRefusal`, never a green toast.
 *
 * A file (permit, hazmat report) is an `ew_job` upload against this job,
 * uploaded when the form is saved; if it does not land, nothing is created.
 */
import React, { useState } from 'react';
import { AlertTriangle, FileText, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  uploadEwJobFile,
  useCreateEwHazmat,
  useCreateEwPermit,
  useCreateEwRentIn,
  useDeleteEwHazmat,
  useDeleteEwPermit,
  useDeleteEwRentIn,
  useEwDailyLogs,
  useEwHazmat,
  useEwPermits,
  useEwRentIns,
  useEwWarnings,
} from '../../hooks/useEwField';
import { blankToNull, catalogLabel, ewDay, ewRefusal, ewRentSkin, fmtMoney, fmtNumber } from '../../lib/earthworks';
import type { EwCatalog, EwDailyLogSummary, EwHazmat, EwPermit, EwRentIn } from '../../types/earthworks';
import {
  EW_DATE_INPUT,
  EW_INPUT,
  EW_PRIMARY,
  EW_SECONDARY,
  EwBadge,
  EwDialog,
  EwEmpty,
  EwField,
  EwFormError,
  EwPanel,
  EwRegister,
  type EwColumn,
} from './EwBits';
import { EwWarningCards, fromOfficeWarning } from './EwWarnings';

// ─── Shared bits ─────────────────────────────────────────────────────────────

function AddButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" className={EW_PRIMARY} onClick={onClick}>
      <Plus className="size-4" aria-hidden="true" />
      {label}
    </button>
  );
}

function RemoveButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" className={EW_SECONDARY} aria-label={label} onClick={(e) => { e.stopPropagation(); onClick(); }}>
      <Trash2 className="size-4" aria-hidden="true" />
      Remove
    </button>
  );
}

function FormButtons({ pending, onCancel, label }: { pending: boolean; onCancel: () => void; label: string }) {
  return (
    <div className="flex flex-wrap justify-end gap-2">
      <button type="button" className={EW_SECONDARY} onClick={onCancel} disabled={pending}>
        Cancel
      </button>
      <button type="submit" className={EW_PRIMARY} disabled={pending}>
        {pending ? 'Saving…' : label}
      </button>
    </div>
  );
}

function RemoveConfirm({
  open,
  title,
  body,
  pending,
  error,
  onClose,
  onConfirm,
}: {
  open: boolean;
  title: string;
  body: string;
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <EwDialog
      open={open}
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={EW_SECONDARY} onClick={onClose} disabled={pending}>
            Keep it
          </button>
          <button type="button" className={EW_PRIMARY} onClick={onConfirm} disabled={pending}>
            {pending ? 'Removing…' : 'Remove'}
          </button>
        </>
      }
    >
      <EwFormError message={error} />
      <p className="text-sm text-fieldiq-text">{body}</p>
    </EwDialog>
  );
}

export function FileLink({ file }: { file: { url: string | null; fileName: string | null } | null }) {
  if (!file?.url) return <>—</>;
  return (
    <a href={file.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 font-bold text-fi-amber hover:underline">
      <FileText className="size-4" aria-hidden="true" />
      {file.fileName || 'File'}
    </a>
  );
}

/** Upload the chosen file (if any) against the job; null when none was chosen. Throws when it did not land. */
export async function uploadIfChosen(jobId: string, file: File | null): Promise<string | null> {
  return file ? uploadEwJobFile(jobId, file) : null;
}

/** The job's own words for an error that may be a thrown upload failure or an API refusal. */
export const writeError = (err: unknown, fallback: string) =>
  err instanceof Error && !(err as { response?: unknown }).response && err.message ? `${err.message}` : ewRefusal(err, fallback);

// ─── Warnings banner ─────────────────────────────────────────────────────────

/** Today's warnings for the job, amber. Read-only here; the hand carries on from the phone. */
export function EwJobWarningsBanner({ jobId, timeZone }: { jobId: string; timeZone: string | null }) {
  const q = useEwWarnings(jobId);
  const warnings = (q.data?.data ?? []).map(fromOfficeWarning);
  if (warnings.length === 0) return null;
  return (
    <div data-testid="ew-job-warnings-banner" className="space-y-2">
      <p className="flex items-center gap-2 text-sm font-extrabold text-white">
        <AlertTriangle className="size-4 text-fi-amber" aria-hidden="true" />
        {warnings.length} warning{warnings.length === 1 ? '' : 's'} today — work is not blocked
      </p>
      <EwWarningCards warnings={warnings} timeZone={q.data?.meta.timeZone ?? timeZone} testId="ew-job-warnings" />
    </div>
  );
}

// ─── Daily logs ──────────────────────────────────────────────────────────────

export function DailyLogsSection({ jobId, onOpen, canWrite = false }: { jobId: string; onOpen: (path: string) => void; canWrite?: boolean }) {
  const q = useEwDailyLogs(jobId);
  const rows = q.data?.data ?? [];
  const today = q.data?.meta?.today ?? null;
  const [day, setDay] = useState('');
  const chosen = day || today || '';
  const columns: EwColumn<EwDailyLogSummary>[] = [
    { key: 'date', header: 'Date', render: (l) => <span className="font-extrabold text-white">{ewDay(l.workDate)}</span> },
    { key: 'work', header: 'Work', render: (l) => (l.workKinds.length ? l.workKinds.map((k) => k.label).join(', ') : '—') },
    { key: 'crew', header: 'Crew hours', align: 'right', render: (l) => fmtNumber(l.crewHours) },
    { key: 'units', header: 'Unit hours', align: 'right', render: (l) => fmtNumber(l.equipmentHours) },
    { key: 'off', header: 'Loads off', align: 'right', render: (l) => fmtNumber(l.loadsHauledOff) },
    { key: 'in', header: 'Loads in', align: 'right', render: (l) => fmtNumber(l.loadsImported) },
    { key: 'dumps', header: 'Dump tickets', align: 'right', render: (l) => fmtNumber(l.dumpTickets) },
    { key: 'photos', header: 'Photos', align: 'right', render: (l) => fmtNumber(l.photos) },
    { key: 'acks', header: 'Warnings carried on', align: 'right', render: (l) => fmtNumber(l.warningsAcknowledged) },
  ];
  return (
    <EwPanel
      eyebrow="Field"
      title="Daily logs"
      testId="ew-job-logs"
      actions={
        canWrite ? (
          <div className="flex items-center gap-2">
            <input
              type="date"
              className={EW_DATE_INPUT}
              aria-label="Log day"
              max={today ?? undefined}
              value={chosen}
              onChange={(e) => setDay(e.target.value)}
            />
            <button type="button" className={EW_PRIMARY} disabled={!chosen} onClick={() => onOpen(`/earthworks/jobs/${jobId}/log/${chosen}`)}>
              <Plus className="size-4" aria-hidden="true" />
              Write a log
            </button>
          </div>
        ) : undefined
      }
    >
      <EwRegister
        query={q}
        rows={rows}
        columns={columns}
        rail={(l) => ((l.warningsAcknowledged ?? 0) > 0 ? 'border-l-fi-amber' : 'border-l-fi-steel')}
        onOpen={(l) => onOpen(`/earthworks/daily-logs/${l.id}`)}
        subject="daily logs"
        testId="ew-job-log"
        minWidth="min-w-[980px]"
        empty={<EwEmpty>No daily log has been written for this job yet. The crew writes them on the phone; the office can write one here.</EwEmpty>}
        card={(l) => (
          <div>
            <p className="text-sm font-extrabold text-white">{ewDay(l.workDate)}</p>
            <p className="mt-0.5 text-xs text-fieldiq-muted">
              Crew {fmtNumber(l.crewHours)} h · units {fmtNumber(l.equipmentHours)} h · loads {fmtNumber(l.loadsHauledOff)} off / {fmtNumber(l.loadsImported)} in
            </p>
          </div>
        )}
      />
    </EwPanel>
  );
}

// ─── Rent-ins ────────────────────────────────────────────────────────────────

const EMPTY_RENT = { vendor_name: '', description: '', vendor_contact: '', unit_number: '', cost_rate: '', rate_unit: '', billable: false, on_rent_on: '', off_rent_on: '', po_number: '', notes: '' };

export function RentInsSection({ jobId, catalog, currency, canWrite }: { jobId: string; catalog: EwCatalog | null; currency: string | null; canWrite: boolean }) {
  const q = useEwRentIns(jobId);
  const add = useCreateEwRentIn();
  const remove = useDeleteEwRentIn();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY_RENT);
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<EwRentIn | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const rows = q.data?.data ?? [];

  const columns: EwColumn<EwRentIn>[] = [
    { key: 'desc', header: 'Unit', render: (r) => <span className="font-extrabold text-white">{r.description || '—'}</span> },
    { key: 'vendor', header: 'Vendor', render: (r) => r.vendorName || '—' },
    { key: 'unit', header: 'Unit #', render: (r) => <span className="font-mono">{r.unitNumber || '—'}</span> },
    { key: 'dates', header: 'On rent', render: (r) => `${ewDay(r.onRentOn)} → ${ewDay(r.offRentOn)}` },
    { key: 'status', header: 'Status', render: (r) => <EwBadge skin={ewRentSkin(r.rentStatus)} value={r.rentStatus} /> },
    { key: 'rate', header: 'Rate', align: 'right', render: (r) => (r.costRate === undefined ? '—' : `${fmtMoney(r.costRate, currency)}${r.rateUnitLabel ? ` / ${r.rateUnitLabel}` : ''}`) },
    { key: 'hours', header: 'Logged hours', align: 'right', render: (r) => fmtNumber(r.loggedHours) },
    ...(canWrite ? [{ key: 'remove', header: '', align: 'right' as const, render: (r: EwRentIn) => <RemoveButton label={`Remove ${r.description}`} onClick={() => { setRemoveError(null); setRemoving(r); }} /> }] : []),
  ];

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.vendor_name.trim()) return setError('Enter the vendor.');
    if (!form.description.trim()) return setError('Describe the unit.');
    if (!form.on_rent_on) return setError('Enter the on-rent day.');
    add.mutate(
      {
        jobId,
        body: {
          vendor_name: form.vendor_name.trim(),
          description: form.description.trim(),
          vendor_contact: blankToNull(form.vendor_contact),
          unit_number: blankToNull(form.unit_number),
          cost_rate: blankToNull(form.cost_rate),
          rate_unit: form.cost_rate.trim() ? form.rate_unit || null : null,
          billable: form.billable,
          on_rent_on: form.on_rent_on,
          off_rent_on: blankToNull(form.off_rent_on),
          po_number: blankToNull(form.po_number),
          notes: blankToNull(form.notes),
        },
      },
      {
        onSuccess: () => {
          toast.success('Rent-in added');
          setAdding(false);
          setForm(EMPTY_RENT);
        },
        onError: (err) => setError(ewRefusal(err, 'The rent-in could not be added.')),
      },
    );
  };

  return (
    <EwPanel
      eyebrow="Equipment"
      title="Rented-in units"
      testId="ew-job-rent-ins"
      actions={canWrite && !adding ? <AddButton label="Add rent-in" onClick={() => { setError(null); setAdding(true); }} /> : undefined}
    >
      {adding && canWrite && (
        <form onSubmit={submit} className="space-y-3 border-b border-fieldiq-border px-4 py-4" data-testid="ew-rent-in-form">
          <EwFormError message={error} />
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <EwField label="Vendor"><input className={EW_INPUT} aria-label="Vendor" value={form.vendor_name} onChange={(e) => setForm({ ...form, vendor_name: e.target.value })} /></EwField>
            <EwField label="Unit description"><input className={EW_INPUT} aria-label="Unit description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></EwField>
            <EwField label="Unit #"><input className={EW_INPUT} aria-label="Rent-in unit number" value={form.unit_number} onChange={(e) => setForm({ ...form, unit_number: e.target.value })} /></EwField>
            <EwField label="Vendor contact"><input className={EW_INPUT} aria-label="Vendor contact" value={form.vendor_contact} onChange={(e) => setForm({ ...form, vendor_contact: e.target.value })} /></EwField>
            <EwField label="On rent"><input type="date" className={EW_DATE_INPUT} aria-label="On rent" value={form.on_rent_on} onChange={(e) => setForm({ ...form, on_rent_on: e.target.value })} /></EwField>
            <EwField label="Off rent"><input type="date" className={EW_DATE_INPUT} aria-label="Off rent" value={form.off_rent_on} onChange={(e) => setForm({ ...form, off_rent_on: e.target.value })} /></EwField>
            <EwField label={`Vendor rate${currency ? ` (${currency})` : ''}`}><input className={EW_INPUT} inputMode="decimal" aria-label="Vendor rate" value={form.cost_rate} onChange={(e) => setForm({ ...form, cost_rate: e.target.value })} /></EwField>
            <EwField label="Per">
              <select className={EW_INPUT} aria-label="Rate unit" value={form.rate_unit} onChange={(e) => setForm({ ...form, rate_unit: e.target.value })}>
                <option value="">—</option>
                {(catalog?.rentRateUnits ?? []).map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
              </select>
            </EwField>
            <EwField label="PO number"><input className={EW_INPUT} aria-label="PO number" value={form.po_number} onChange={(e) => setForm({ ...form, po_number: e.target.value })} /></EwField>
            <EwField label="Notes"><input className={EW_INPUT} aria-label="Rent-in notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></EwField>
            <label className="flex items-center gap-2 text-sm text-fieldiq-text sm:pt-6">
              <input type="checkbox" checked={form.billable} onChange={(e) => setForm({ ...form, billable: e.target.checked })} />
              Billable to the customer
            </label>
          </div>
          <FormButtons pending={add.isPending} onCancel={() => setAdding(false)} label="Add rent-in" />
        </form>
      )}
      <EwRegister
        query={q}
        rows={rows}
        columns={columns}
        rail={(r) => ewRentSkin(r.rentStatus).rail}
        subject="rent-ins"
        testId="ew-rent-in"
        minWidth="min-w-[980px]"
        empty={<EwEmpty>No unit is rented in for this job.</EwEmpty>}
        card={(r) => (
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-extrabold text-white">{r.description || '—'}</p>
              <p className="mt-0.5 text-xs text-fieldiq-muted">{r.vendorName || '—'} · {ewDay(r.onRentOn)} → {ewDay(r.offRentOn)}</p>
            </div>
            {canWrite && <RemoveButton label={`Remove ${r.description}`} onClick={() => { setRemoveError(null); setRemoving(r); }} />}
          </div>
        )}
      />
      <RemoveConfirm
        open={!!removing}
        title="Remove rent-in"
        body={`Takes ${removing?.description ?? 'this unit'} off the job. A unit with logged hours cannot be removed — take it off rent instead.`}
        pending={remove.isPending}
        error={removeError}
        onClose={() => setRemoving(null)}
        onConfirm={() =>
          removing &&
          remove.mutate(removing.id, {
            onSuccess: () => { toast.success('Rent-in removed'); setRemoving(null); },
            onError: (err) => setRemoveError(ewRefusal(err, 'The rent-in could not be removed.')),
          })
        }
      />
    </EwPanel>
  );
}

// ─── Permits ─────────────────────────────────────────────────────────────────

const EMPTY_PERMIT = { kind: 'demolition', permit_number: '', issuer: '', issued_on: '', expires_on: '', notes: '' };

export function PermitsSection({ jobId, catalog, canWrite }: { jobId: string; catalog: EwCatalog | null; canWrite: boolean }) {
  const q = useEwPermits(jobId);
  const add = useCreateEwPermit();
  const remove = useDeleteEwPermit();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY_PERMIT);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<EwPermit | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const rows = q.data?.data ?? [];

  const columns: EwColumn<EwPermit>[] = [
    { key: 'number', header: 'Permit #', render: (p) => <span className="font-mono font-extrabold text-white">{p.permitNumber || '—'}</span> },
    { key: 'kind', header: 'Kind', render: (p) => p.kindLabel || catalogLabel(catalog?.permitKinds, p.kind) },
    { key: 'issuer', header: 'Issuer', render: (p) => p.issuer || '—' },
    { key: 'issued', header: 'Issued', render: (p) => ewDay(p.issuedOn) },
    { key: 'expires', header: 'Expires', render: (p) => (p.expired ? <span className="font-bold text-fi-red">{ewDay(p.expiresOn)} · expired</span> : ewDay(p.expiresOn)) },
    { key: 'file', header: 'File', render: (p) => <FileLink file={p.file} /> },
    ...(canWrite ? [{ key: 'remove', header: '', align: 'right' as const, render: (p: EwPermit) => <RemoveButton label={`Remove permit ${p.permitNumber}`} onClick={() => { setRemoveError(null); setRemoving(p); }} /> }] : []),
  ];

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.permit_number.trim()) return setError('Enter the permit number.');
    setBusy(true);
    try {
      const uploadId = await uploadIfChosen(jobId, file);
      await add.mutateAsync({
        jobId,
        body: {
          kind: form.kind,
          permit_number: form.permit_number.trim(),
          issuer: blankToNull(form.issuer),
          issued_on: blankToNull(form.issued_on),
          expires_on: blankToNull(form.expires_on),
          upload_id: uploadId,
          notes: blankToNull(form.notes),
        },
      });
      toast.success('Permit added');
      setAdding(false);
      setForm(EMPTY_PERMIT);
      setFile(null);
    } catch (err) {
      setError(writeError(err, 'The permit could not be added.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <EwPanel
      eyebrow="Compliance"
      title="Permits"
      testId="ew-job-permits"
      actions={canWrite && !adding ? <AddButton label="Add permit" onClick={() => { setError(null); setAdding(true); }} /> : undefined}
    >
      {adding && canWrite && (
        <form onSubmit={submit} className="space-y-3 border-b border-fieldiq-border px-4 py-4" data-testid="ew-permit-form">
          <EwFormError message={error} />
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <EwField label="Kind">
              <select className={EW_INPUT} aria-label="Permit kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
                {(catalog?.permitKinds ?? [{ value: 'demolition', label: 'Demolition' }]).map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
            </EwField>
            <EwField label="Permit #"><input className={EW_INPUT} aria-label="Permit number" value={form.permit_number} onChange={(e) => setForm({ ...form, permit_number: e.target.value })} /></EwField>
            <EwField label="Issuer"><input className={EW_INPUT} aria-label="Issuer" value={form.issuer} onChange={(e) => setForm({ ...form, issuer: e.target.value })} /></EwField>
            <EwField label="Issued"><input type="date" className={EW_DATE_INPUT} aria-label="Issued on" value={form.issued_on} onChange={(e) => setForm({ ...form, issued_on: e.target.value })} /></EwField>
            <EwField label="Expires"><input type="date" className={EW_DATE_INPUT} aria-label="Expires on" value={form.expires_on} onChange={(e) => setForm({ ...form, expires_on: e.target.value })} /></EwField>
            <EwField label="Permit file" hint="Optional">
              <input type="file" accept="application/pdf,image/*" aria-label="Permit file" className="text-sm text-fieldiq-text" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            </EwField>
            <EwField label="Notes"><input className={EW_INPUT} aria-label="Permit notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></EwField>
          </div>
          <FormButtons pending={busy} onCancel={() => setAdding(false)} label="Add permit" />
        </form>
      )}
      <EwRegister
        query={q}
        rows={rows}
        columns={columns}
        rail={(p) => (p.expired ? 'border-l-fi-red' : 'border-l-fi-green')}
        subject="permits"
        testId="ew-permit"
        minWidth="min-w-[880px]"
        empty={<EwEmpty>No permit is on file for this job.</EwEmpty>}
        card={(p) => (
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate font-mono text-sm font-extrabold text-white">{p.permitNumber}</p>
              <p className="mt-0.5 text-xs text-fieldiq-muted">{p.kindLabel || p.kind} · expires {ewDay(p.expiresOn)}{p.expired ? ' · expired' : ''}</p>
            </div>
            {canWrite && <RemoveButton label={`Remove permit ${p.permitNumber}`} onClick={() => { setRemoveError(null); setRemoving(p); }} />}
          </div>
        )}
      />
      <RemoveConfirm
        open={!!removing}
        title="Remove permit"
        body={`Removes permit ${removing?.permitNumber ?? ''} from the job.`}
        pending={remove.isPending}
        error={removeError}
        onClose={() => setRemoving(null)}
        onConfirm={() =>
          removing &&
          remove.mutate(removing.id, {
            onSuccess: () => { toast.success('Permit removed'); setRemoving(null); },
            onError: (err) => setRemoveError(ewRefusal(err, 'The permit could not be removed.')),
          })
        }
      />
    </EwPanel>
  );
}

// ─── Hazmat ──────────────────────────────────────────────────────────────────

const EMPTY_HAZMAT = { assessor: '', assessed_on: '', result: '', materials_assessed: '', abatement_cleared_on: '', notes: '' };

export function HazmatSection({ jobId, catalog, canWrite }: { jobId: string; catalog: EwCatalog | null; canWrite: boolean }) {
  const q = useEwHazmat(jobId);
  const add = useCreateEwHazmat();
  const remove = useDeleteEwHazmat();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY_HAZMAT);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<EwHazmat | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const rows = q.data?.data ?? [];

  const columns: EwColumn<EwHazmat>[] = [
    { key: 'assessed', header: 'Assessed', render: (h) => <span className="font-extrabold text-white">{ewDay(h.assessedOn)}</span> },
    { key: 'assessor', header: 'Assessor', render: (h) => h.assessor || '—' },
    { key: 'result', header: 'Result', render: (h) => h.resultLabel || catalogLabel(catalog?.hazmatResults, h.result) },
    { key: 'materials', header: 'Materials assessed', render: (h) => h.materialsAssessed || '—' },
    { key: 'cleared', header: 'Abatement cleared', render: (h) => ewDay(h.abatementClearedOn) },
    { key: 'file', header: 'Report', render: (h) => <FileLink file={h.file} /> },
    ...(canWrite ? [{ key: 'remove', header: '', align: 'right' as const, render: (h: EwHazmat) => <RemoveButton label={`Remove the ${ewDay(h.assessedOn)} assessment`} onClick={() => { setRemoveError(null); setRemoving(h); }} /> }] : []),
  ];

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.assessor.trim()) return setError('Enter who assessed it.');
    if (!form.assessed_on) return setError('Enter the day it was assessed.');
    if (!form.result) return setError('Choose the result.');
    setBusy(true);
    try {
      const uploadId = await uploadIfChosen(jobId, file);
      await add.mutateAsync({
        jobId,
        body: {
          assessor: form.assessor.trim(),
          assessed_on: form.assessed_on,
          result: form.result,
          materials_assessed: blankToNull(form.materials_assessed),
          abatement_cleared_on: blankToNull(form.abatement_cleared_on),
          upload_id: uploadId,
          notes: blankToNull(form.notes),
        },
      });
      toast.success('Hazmat assessment added');
      setAdding(false);
      setForm(EMPTY_HAZMAT);
      setFile(null);
    } catch (err) {
      setError(writeError(err, 'The assessment could not be added.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <EwPanel
      eyebrow="Compliance"
      title="Hazardous materials assessments"
      testId="ew-job-hazmat"
      actions={canWrite && !adding ? <AddButton label="Add assessment" onClick={() => { setError(null); setAdding(true); }} /> : undefined}
    >
      {adding && canWrite && (
        <form onSubmit={submit} className="space-y-3 border-b border-fieldiq-border px-4 py-4" data-testid="ew-hazmat-form">
          <EwFormError message={error} />
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <EwField label="Assessor"><input className={EW_INPUT} aria-label="Assessor" value={form.assessor} onChange={(e) => setForm({ ...form, assessor: e.target.value })} /></EwField>
            <EwField label="Assessed"><input type="date" className={EW_DATE_INPUT} aria-label="Assessed on" value={form.assessed_on} onChange={(e) => setForm({ ...form, assessed_on: e.target.value })} /></EwField>
            <EwField label="Result">
              <select className={EW_INPUT} aria-label="Result" value={form.result} onChange={(e) => setForm({ ...form, result: e.target.value })}>
                <option value="">Choose…</option>
                {(catalog?.hazmatResults ?? []).map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </EwField>
            <EwField label="Abatement cleared"><input type="date" className={EW_DATE_INPUT} aria-label="Abatement cleared on" value={form.abatement_cleared_on} onChange={(e) => setForm({ ...form, abatement_cleared_on: e.target.value })} /></EwField>
            <EwField label="Materials assessed"><input className={EW_INPUT} aria-label="Materials assessed" value={form.materials_assessed} onChange={(e) => setForm({ ...form, materials_assessed: e.target.value })} /></EwField>
            <EwField label="Report" hint="Optional">
              <input type="file" accept="application/pdf,image/*" aria-label="Hazmat report" className="text-sm text-fieldiq-text" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            </EwField>
            <EwField label="Notes"><input className={EW_INPUT} aria-label="Hazmat notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></EwField>
          </div>
          <FormButtons pending={busy} onCancel={() => setAdding(false)} label="Add assessment" />
        </form>
      )}
      <EwRegister
        query={q}
        rows={rows}
        columns={columns}
        rail={(h) => (h.result === 'found' && !h.abatementClearedOn ? 'border-l-fi-amber' : 'border-l-fi-steel')}
        subject="hazmat assessments"
        testId="ew-hazmat"
        minWidth="min-w-[880px]"
        empty={<EwEmpty>No hazardous materials assessment is on file for this job.</EwEmpty>}
        card={(h) => (
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-extrabold text-white">{ewDay(h.assessedOn)} · {h.resultLabel || h.result}</p>
              <p className="mt-0.5 text-xs text-fieldiq-muted">{h.assessor}</p>
            </div>
            {canWrite && <RemoveButton label={`Remove the ${ewDay(h.assessedOn)} assessment`} onClick={() => { setRemoveError(null); setRemoving(h); }} />}
          </div>
        )}
      />
      <RemoveConfirm
        open={!!removing}
        title="Remove assessment"
        body="Removes this hazardous materials assessment from the job."
        pending={remove.isPending}
        error={removeError}
        onClose={() => setRemoving(null)}
        onConfirm={() =>
          removing &&
          remove.mutate(removing.id, {
            onSuccess: () => { toast.success('Assessment removed'); setRemoving(null); },
            onError: (err) => setRemoveError(ewRefusal(err, 'The assessment could not be removed.')),
          })
        }
      />
    </EwPanel>
  );
}
